import { Colors } from '@oy/uicore';
import {
  Button,
  ButtonType,
  ButtonWithIcon,
  Loader,
  Text,
  TextField,
  toast,
} from '@oy/uidesktop';
import { SystemIcon } from '@oy/uiicon';
import { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { signLegalDocumentManual } from 'src/clients';
import { BusinessTypeValue } from 'src/constants';
import { onboardingOyEmail } from 'src/constants/Email';
import { useBusinessTypeData, useTranslate } from 'src/hooks';
import { ACTION_TYPES } from 'src/redux/types/signLegalDocumentType';
import { reportFunctionError } from 'src/utilities/sentry';
import { EMAIL_CONTENT } from '../../constants';
import { SignLegalDocumentModalContext } from '../../useSignLegalDocumentModalContext';
import { RequestSignByEmailModal } from '../ConfirmationModal';
import { DocumentCard } from '../DocumentCard/DocumentCard';
import { getStepTitle } from '../SignLegalDocumentModalHeader/helper';
import styles from './ListDocument.module.css';

type ListDocumentProps = {
  onDownload: () => void;
  onClickReadDocument: (id: string) => void;
  setShowModalEmailDidntOpen: (value: boolean) => void;
};

export const ListDocument = ({
  onDownload,
  onClickReadDocument,
  setShowModalEmailDidntOpen,
}: ListDocumentProps) => {
  const { getTranslation, lang } = useTranslate();
  const { data: businessData } = useBusinessTypeData();

  const dispatch = useDispatch();

  const {
    allDocument,
    payloadToSignDocuments,
    agreementNumber,
    onChangeAgreementNumber,
  } = useContext(SignLegalDocumentModalContext);
  const [showModalRequestSignByEmail, setShowModalRequestSignByEmail] =
    useState(false);
  const [loading, setIsLoading] = useState(false);

  const onOpenModalRequestSignByEmail = () => {
    setShowModalRequestSignByEmail(true);
  };

  const onCloseModalRequestSignByEmail = () => {
    setShowModalRequestSignByEmail(false);
  };

  const onRequestSignByEmail = async () => {
    try {
      setIsLoading(true);
      const response = await signLegalDocumentManual();
      if (response.data.success) {
        const recipientEmail = onboardingOyEmail;
        const subject = EMAIL_CONTENT.subject[lang];
        const body = EMAIL_CONTENT.body[lang];

        const mailtoUrl =
          'mailto:' +
          encodeURIComponent(recipientEmail) +
          '?subject=' +
          encodeURIComponent(subject) +
          '&body=' +
          encodeURIComponent(body);
        window.location.href = mailtoUrl;
        onCloseModalRequestSignByEmail();
        dispatch({ type: ACTION_TYPES.CLOSE_MODAL });
        setShowModalEmailDidntOpen(true);
      } else {
        toast.error(getTranslation('failed_request_sign_document_by_email'));
      }
    } catch (e) {
      reportFunctionError({
        name: 'onRequestSignByEmail PKS',
        message: 'Failed to request sign by email PKS b2xportal',
        domain: 'dashboard',
        exception: e,
      });
      toast.error(getTranslation('failed_request_sign_document_by_email'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.listContainer}>
      <div className={styles.mainContent}>
        <div className={styles.listHeader}>
          <div className={styles.listHeaderContent}>
            <Text textStyle={Text.Style.HeadingMd}>
              {getTranslation('common_PKS')}
            </Text>
            <Text textStyle={Text.Style.BodyMdRegular}>
              {getTranslation('pks_subtitle')}
            </Text>
          </div>
          <ButtonWithIcon
            buttonType={ButtonType.Text}
            iconName="download"
            onClick={onDownload}
            className={styles.downloadButton}
          >
            {getTranslation('common_downloadDoc')}
          </ButtonWithIcon>
        </div>

        <div className={styles.listDataContainer}>
          {allDocument.map(file => (
            <DocumentCard
              key={file.id}
              needToSign={
                file.status === 'NEED_TO_SIGN' &&
                !payloadToSignDocuments.includes(file.id)
              }
              title={getStepTitle(file)}
              documentId={file.id}
              onClickReadDocument={onClickReadDocument}
            />
          ))}
        </div>

        <div className={styles.agreementNumberSection}>
          <Text color={Colors.BLACK20} textStyle={Text.Style.BodyMdSemibold}>
            {getTranslation('agreement_number_label_optional').toUpperCase()}
          </Text>
          <TextField
            value={agreementNumber ?? ''}
            onChange={e => onChangeAgreementNumber?.(e.target.value)}
            placeholder={getTranslation(
              'common_agreement_number_placeholder',
            ).toLowerCase()}
          />
        </div>
      </div>
      {businessData.business_type === BusinessTypeValue.Corporate && (
        <div className={styles.bottomSection}>
          <SystemIcon name="pen" size={24} color={Colors.BLACK100} />
          <Text textStyle={Text.Style.BodyMdSemibold}>
            {getTranslation('pks_manual_sign_label')}
          </Text>
          <Button
            onClick={onOpenModalRequestSignByEmail}
            size={Button.Size.Small}
            buttonType={Button.Type.Outlined}
          >
            {getTranslation('common_request')}
          </Button>
        </div>
      )}
      <RequestSignByEmailModal
        showModal={showModalRequestSignByEmail}
        onCloseModal={onCloseModalRequestSignByEmail}
        onSendEmail={onRequestSignByEmail}
      />

      {loading && <Loader isUsingBackdrop />}
    </div>
  );
};
